import {CommonModule, DOCUMENT} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatMomentDateModule} from '@angular/material-moment-adapter';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatSelectModule} from '@angular/material/select';
import {MatSortModule, Sort} from '@angular/material/sort';
import {MatTableModule} from '@angular/material/table';
import {MatTabsModule} from '@angular/material/tabs';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {RouterLink} from '@angular/router';
import {TranslatePipe} from '@ngx-translate/core';
import {debounceTime, distinctUntilChanged, Subject} from 'rxjs';
import {FilterOptionGroup} from '../../../core/definitions/extended-search/filter-option-group';
import {SettingsService} from '../../../core/settings.service';
import {OperationService} from '../../../operations/operation.service';
import {getDateFormat} from '../../../object-edit/edit-field-date-picker/edit-field-date-picker.component';

import {
  InventoryReportApiService,
  DeletedInventoryReportItem,
  DeletedInventoryReportSort,
  InventoryReportExportColumn,
  InventoryReportGeneration,
  InventoryReportItem,
  InventoryReportKind,
  InventoryReportPage,
  InventoryReportSort,
  InventoryReportSortDirection,
} from './inventory-report-api.service';


const GENERATION_POLL_INTERVAL_MS = 5000;
// The UI selects "All" explicitly; the API represents it as no type filter.
const ALL_OBJECT_TYPES = '__all__';
const EXPORT_COLUMN_BY_TABLE_COLUMN: Record<
  string,
  InventoryReportExportColumn
> = {
  deletedAt: 'deleted_at',
  deletedBy: 'deleted_by',
  deletionReason: 'deletion_reason',
  artifactId: 'artifact_id',
  identificationNumber: 'identification_number',
  designation: 'designations',
  title: 'title',
  count: 'count',
  description: 'description',
  motifDescription: 'motif_description',
  placement: 'current_placement',
  collection: 'collection',
  owner: 'owner',
  registeredAt: 'registered_at',
  registeredBy: 'registered_by',
  internalInformation: 'internal_information',
  salvagePriority: 'salvage_priority_registered',
};


@Component({
  selector: 'app-admin-service-inventory-report',
  imports: [
    CommonModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    ReactiveFormsModule,
    RouterLink,
    TranslatePipe,
  ],
  templateUrl: './admin-service-inventory-report.component.html',
  styleUrl: './admin-service-inventory-report.component.scss',
  providers: [
    {provide: MAT_DATE_FORMATS, useFactory: getDateFormat, deps: [SettingsService]},
  ],
})
export class AdminServiceInventoryReportComponent implements OnInit {
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly reportApi = inject(InventoryReportApiService);
  private readonly settings = inject(SettingsService);
  private readonly operationService = inject(OperationService);
  private destroyed = false;
  private generationPollTimeout?: number;
  private generationRequestId = 0;
  private loadRequestId = 0;
  private deletedLoadRequestId = 0;
  private readonly previewChanges = new Subject<void>();
  private previewInFlight = false;
  private previewPending = false;
  readonly previewLimit = 200;
  expanded = false;

  private readonly columnOrder = [
    'image',
    'identificationNumber',
    'designation',
    'title',
    'count',
    'description',
    'motifDescription',
    'placement',
    'collection',
    'owner',
    'registeredAt',
    'registeredBy',
    'internalInformation',
    'salvagePriority',
  ];
  private readonly requiredColumns = new Set([
    'identificationNumber',
    'designation',
    'count',
    'placement',
  ]);

  readonly optionalColumns = [
    {
      key: 'image',
      label: 'TRANS__INVENTORY_REPORT__COLUMN__IMAGE',
      enabled: true,
    },
    {
      key: 'title',
      label: 'TRANS__INVENTORY_REPORT__COLUMN__TITLE',
      enabled: true,
    },
    {
      key: 'description',
      label: 'TRANS__INVENTORY_REPORT__COLUMN__DESCRIPTION',
      enabled: true,
    },
    {
      key: 'motifDescription',
      label: 'TRANS__INVENTORY_REPORT__COLUMN__MOTIF_DESCRIPTION',
      enabled: false,
    },
    {
      key: 'collection',
      label: 'TRANS__FIELD__COLLECTION',
      enabled: true,
    },
    {
      key: 'owner',
      label: 'TRANS__INVENTORY_REPORT__COLUMN__OWNER',
      enabled: true,
    },
    {
      key: 'registeredAt',
      label: 'TRANS__INVENTORY_REPORT__COLUMN__REGISTERED_AT',
      enabled: false,
    },
    {
      key: 'registeredBy',
      label: 'TRANS__INVENTORY_REPORT__COLUMN__REGISTERED_BY',
      enabled: false,
    },
    {
      key: 'internalInformation',
      label: 'TRANS__INVENTORY_REPORT__COLUMN__INTERNAL_INFORMATION',
      enabled: false,
    },
    {
      key: 'salvagePriority',
      label: 'TRANS__INVENTORY_REPORT__COLUMN__SALVAGE_PRIORITY',
      enabled: true,
    },
  ];
  readonly descriptionLengthOptions = [
    {value: 100, translationParams: {count: 100}},
    {value: 250, translationParams: {count: 250}},
    {value: 500, translationParams: {count: 500}},
    {value: 0, translationParams: null},
  ];
  readonly filterControl = new FormControl('', {nonNullable: true});
  readonly deletedFilterControl = new FormControl('', {nonNullable: true});

  displayedColumns = this.buildDisplayedColumns();
  deletedColumns = this.buildDeletedColumns();
  descriptionMaxLength = 250;
  items: InventoryReportItem[] = [];
  loading = false;
  loaded = false;
  errorMessage = '';
  failedImageIds = new Set<string>();
  printedAt = new Date();
  preparingPrint = false;
  sortField: InventoryReportSort = 'registered_at';
  sortDirection: InventoryReportSortDirection = 'asc';
  filterText = '';
  objectTypeGroups: FilterOptionGroup[] = [];
  deletedObjectTypeGroups: FilterOptionGroup[] = [];
  readonly allObjectTypes = ALL_OBJECT_TYPES;
  selectedObjectTypes: string[] = [ALL_OBJECT_TYPES];
  objectTypeFilterSupported = true;
  generationErrorMessage = '';
  generationLoading = false;
  generationSupported = true;
  activeGeneration: InventoryReportGeneration | null = null;
  latestGeneration: InventoryReportGeneration | null = null;
  downloadingFile = false;
  selectedTab = 0;
  deletedFromDate = this.dateValue(new Date(new Date().getFullYear(), 0, 1));
  deletedToDate = this.dateValue(new Date());
  deletedItems: DeletedInventoryReportItem[] = [];
  deletedSortField: DeletedInventoryReportSort = 'deleted_at';
  deletedSortDirection: InventoryReportSortDirection = 'desc';
  deletedLoading = false;
  deletedError = false;
  deletedLoaded = false;
  deletedFilterText = '';
  deletedSelectedObjectTypes: string[] = [ALL_OBJECT_TYPES];

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      this.clearGenerationPoll();
      ++this.generationRequestId;
      ++this.loadRequestId;
      ++this.deletedLoadRequestId;
    });
    this.previewChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => void this.fetchReport());
    this.filterControl.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(value => {
      const filterText = value.trim();
      if (filterText === this.filterText) {
        return;
      }
      this.filterText = filterText;
      this.clearPreview(false);
    });
    this.deletedFilterControl.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(value => {
      const filterText = value.trim();
      if (filterText !== this.deletedFilterText) {
        this.deletedFilterText = filterText;
        this.clearPreview(true);
      }
    });
    void this.fetchReport();
    void this.loadGenerationState();
  }

  objectTypesChanged(objectTypes: string[]): void {
    this.selectedObjectTypes = this.normalizeObjectTypes(
      objectTypes, this.selectedObjectTypes);
    this.clearPreview(false);
  }

  tabChanged(index: number): void {
    if (index === this.selectedTab) {
      return;
    }
    this.selectedTab = index;
    this.activeGeneration = null;
    this.latestGeneration = null;
    this.generationLoading = false;
    this.downloadingFile = false;
    this.clearPreview(index === 1);
    void this.loadGenerationState();
  }

  deletedObjectTypesChanged(objectTypes: string[]): void {
    this.deletedSelectedObjectTypes = this.normalizeObjectTypes(
      objectTypes, this.deletedSelectedObjectTypes);
    this.clearPreview(true);
  }

  deletedDatesChanged(): void {
    this.clearPreview(true);
  }

  // Hide stale rows immediately, but coalesce rapid changes before querying.
  private clearPreview(deleted: boolean): void {
    this.previewPending = false;
    if (deleted) {
      ++this.deletedLoadRequestId;
      this.deletedItems = [];
      this.deletedError = false;
      this.deletedLoaded = false;
    } else {
      ++this.loadRequestId;
      this.items = [];
      this.errorMessage = '';
      this.loaded = false;
    }
    this.previewChanges.next();
  }

  async fetchReport(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    // One preview at a time across both tabs. Only the latest criteria are
    // fetched afterwards; cancelling HTTP alone would not stop the DB query.
    if (this.previewInFlight) {
      this.previewPending = true;
      return;
    }
    this.previewInFlight = true;
    this.previewPending = false;
    try {
      if (this.selectedTab === 1) {
        await this.loadDeletedPage();
      } else {
        await this.loadPage();
      }
    } finally {
      this.previewInFlight = false;
      if (this.previewPending && !this.destroyed) {
        void this.fetchReport();
      }
    }
  }

  deletedDateRangeValid(): boolean {
    const start = Date.parse(`${this.deletedFromDate}T00:00:00Z`);
    const end = Date.parse(`${this.deletedToDate}T00:00:00Z`);
    return Number.isFinite(start) && Number.isFinite(end) &&
      new Date(start).toISOString().slice(0, 10) === this.deletedFromDate &&
      new Date(end).toISOString().slice(0, 10) === this.deletedToDate &&
      this.deletedFromDate >= '0001-01-01' && this.deletedToDate < '9999-12-31' &&
      end >= start;
  }

  async retry(): Promise<void> {
    await this.fetchReport();
  }

  async sortChanged(sort: Sort): Promise<void> {
    if (!sort.direction || this.currentLoading) {
      return;
    }
    if (this.selectedTab === 1) {
      this.deletedSortField = sort.active as DeletedInventoryReportSort;
      this.deletedSortDirection = sort.direction;
    } else {
      this.sortField = sort.active as InventoryReportSort;
      this.sortDirection = sort.direction;
    }
    this.clearPreview(this.selectedTab === 1);
  }

  async generateFile(): Promise<void> {
    const requestId = this.generationRequestId;
    const deleted = this.selectedTab === 1;
    this.generationLoading = true;
    this.generationErrorMessage = '';
    try {
      await this.reportApi.createGeneration({
        columns: this.exportColumns(),
        description_max_length: this.descriptionMaxLength,
        sort: deleted ? this.deletedSortField : this.sortField,
        direction: deleted ? this.deletedSortDirection : this.sortDirection,
        filter: deleted ? this.deletedFilterText : this.filterText,
        object_types: this.filterObjectTypes(deleted
          ? this.deletedSelectedObjectTypes : this.selectedObjectTypes),
        ...(deleted ? {from_date: this.deletedFromDate, to_date: this.deletedToDate} : {}),
      }, this.reportKind);
      // Keep global job status running even if the user has left this page.
      this.operationService.notifyJobQueued();
      if (requestId === this.generationRequestId) {
        this.generationLoading = false;
        await this.loadGenerationState();
      }
    } catch (error: unknown) {
      if (requestId !== this.generationRequestId || this.destroyed) {
        return;
      }
      if (this.statusFromError(error) === 404) {
        this.generationSupported = false;
      } else if (this.statusFromError(error) === 409) {
        // A different order won the race. Show its queued/running status;
        // do not pretend the user's selected options were submitted.
        await this.loadGenerationState(requestId);
        if (requestId === this.generationRequestId && !this.destroyed) {
          this.generationErrorMessage = 'TRANS__INVENTORY_REPORT__FILE_GENERATION_FAILED';
        }
      } else {
        this.generationErrorMessage =
          'TRANS__INVENTORY_REPORT__FILE_GENERATION_FAILED';
      }
    } finally {
      if (requestId === this.generationRequestId) {
        this.generationLoading = false;
      }
    }
  }

  async downloadLatestFile(): Promise<void> {
    if (!this.latestGeneration?.download_available) {
      return;
    }
    const requestId = this.generationRequestId;
    this.downloadingFile = true;
    this.generationErrorMessage = '';
    try {
      const download = await this.reportApi.getGenerationDownload(
        this.latestGeneration.job_id, this.reportKind,
      );
      if (requestId !== this.generationRequestId || this.destroyed) {
        return;
      }
      this.document.defaultView?.location.assign(download.url);
    } catch {
      if (requestId !== this.generationRequestId || this.destroyed) {
        return;
      }
      this.generationErrorMessage =
        'TRANS__INVENTORY_REPORT__FILE_DOWNLOAD_FAILED';
    } finally {
      if (requestId === this.generationRequestId) {
        this.downloadingFile = false;
      }
    }
  }

  generationStatusTranslation(status: InventoryReportGeneration['status']): string {
    const translations: Record<InventoryReportGeneration['status'], string> = {
      registered: 'TRANS__JOB_STATUS__QUEUED',
      in_progress: 'TRANS__JOB_STATUS__IN_PROGRESS',
      finished: 'TRANS__JOB_STATUS__FINISHED',
      downloaded: 'TRANS__JOB_STATUS__FINISHED',
      failed: 'TRANS__JOB_STATUS__FAILED',
    };
    return translations[status];
  }

  async printReport(): Promise<void> {
    const printClass = 'inventory-report-print';
    const pageStyle = this.createPrintPageStyle();
    const reportImages = this.getReportImages();
    const cleanup = () => {
      this.document.body.classList.remove(printClass);
      pageStyle.remove();
      this.changeDetector.detectChanges();
    };

    this.preparingPrint = true;
    this.printedAt = new Date();
    this.document.body.classList.add(printClass);
    this.changeDetector.detectChanges();

    try {
      await this.waitForReportImages(reportImages);
      window.addEventListener('afterprint', cleanup, {once: true});
      window.print();
    } catch {
      cleanup();
    } finally {
      this.preparingPrint = false;
      this.changeDetector.detectChanges();
    }
  }

  designations(item: Pick<InventoryReportItem, 'designations'>): string {
    return item.designations?.join(', ') ?? '';
  }

  imageFailed(artifactId: string): void {
    this.failedImageIds.add(artifactId);
  }

  toggleColumn(key: string, enabled: boolean): void {
    const option = this.optionalColumns.find(column => column.key === key);
    if (option) {
      option.enabled = enabled;
      this.displayedColumns = this.buildDisplayedColumns();
      this.deletedColumns = this.buildDeletedColumns();
    }
  }

  truncateDescription(description: string | null): string {
    if (!description || this.descriptionMaxLength === 0 ||
        description.length <= this.descriptionMaxLength) {
      return description ?? '';
    }
    return `${description.slice(0, this.descriptionMaxLength).trimEnd()}…`;
  }

  get reportKind(): InventoryReportKind {
    return this.selectedTab === 0 ? 'inventory' : 'deleted';
  }

  get currentItems(): (InventoryReportItem | DeletedInventoryReportItem)[] {
    return this.selectedTab === 0 ? this.items : this.deletedItems;
  }

  get currentLoading(): boolean {
    // A preview may be waiting for the other tab's request to finish.
    return this.loading || this.deletedLoading;
  }

  get currentLoaded(): boolean {
    return this.selectedTab === 0 ? this.loaded : this.deletedLoaded;
  }

  get currentError(): string {
    if (this.selectedTab === 1 && !this.deletedDateRangeValid()) {
      return 'TRANS__VALIDATE__INVALID_DATE';
    }
    return this.selectedTab === 0 ? this.errorMessage
      : this.deletedError ? 'TRANS__INVENTORY_REPORT__LOAD_FAILED' : '';
  }

  private buildDisplayedColumns(): string[] {
    const enabledColumns = new Set(
      this.optionalColumns
        .filter(column => column.enabled)
        .map(column => column.key),
    );
    return this.columnOrder.filter(
      column => this.requiredColumns.has(column) || enabledColumns.has(column),
    );
  }

  private buildDeletedColumns(): string[] {
    return [
      'identificationNumber',
      ...this.displayedColumns.filter(column =>
        column !== 'identificationNumber' && column !== 'image' &&
        column !== 'salvagePriority' && column !== 'placement'),
      'deletedAt', 'deletedBy', 'deletionReason', 'artifactId',
    ];
  }

  private normalizeObjectTypes(next: string[], previous: string[]): string[] {
    if (next.includes(ALL_OBJECT_TYPES)) {
      return !previous.includes(ALL_OBJECT_TYPES) || next.length === 1
        ? [ALL_OBJECT_TYPES]
        : next.filter(type => type !== ALL_OBJECT_TYPES);
    }
    return next.length ? next : [ALL_OBJECT_TYPES];
  }

  private filterObjectTypes(types: string[]): string[] {
    return types.includes(ALL_OBJECT_TYPES) ? [] : types;
  }

  private async loadPage(): Promise<void> {
    if (this.loading) {
      return;
    }
    const requestId = ++this.loadRequestId;
    this.loading = true;
    this.errorMessage = '';
    try {
      await this.loadObjectTypes();
      if (requestId !== this.loadRequestId) {
        return;
      }
      const page = await this.reportApi.getPage(
        this.previewLimit,
        0,
        this.sortField,
        this.sortDirection,
        this.filterText,
        this.filterObjectTypes(this.selectedObjectTypes),
      );
      if (requestId !== this.loadRequestId) {
        return;
      }
      if (!this.isInventoryReportPage(page)) {
        throw new Error('Invalid inventory report response');
      }
      this.failedImageIds.clear();
      this.items = page.items;
      this.loaded = true;
      this.objectTypeFilterSupported = page.object_type_filter_supported === true;
    } catch (error: unknown) {
      if (requestId !== this.loadRequestId) {
        return;
      }
      this.errorMessage = this.statusFromError(error) === 404
        ? 'TRANS__INVENTORY_REPORT__BACKEND_UNAVAILABLE'
        : 'TRANS__INVENTORY_REPORT__LOAD_FAILED';
    } finally {
      this.loading = false;
    }
  }

  private async loadObjectTypes(): Promise<void> {
    if (this.objectTypeGroups.length && this.deletedObjectTypeGroups.length) {
      return;
    }
    // Direct navigation can reach this page before SettingsService is ready.
    const types = this.settings.getClientConfig()?.OBJECT_TYPES ??
      await this.reportApi.getObjectTypes();
    const metaTypes = (names: string[]) => new Set(
      types.filter(type => names.includes(type.type))
        .map(type => type.meta_type_id),
    );
    const artifactMetaTypes = metaTypes(['object', 'photograph_copy']);
    const deletedMetaTypes = metaTypes([
      'object', 'photograph_copy', 'plant', 'unknown']);
    const group = (allowed: Set<string>): FilterOptionGroup[] => {
      const options = types.filter(type =>
        type.superobject_type_id && type.type !== 'object' &&
        allowed.has(type.meta_type_id)).map(type => ({
          id: type.superobject_type_id,
          name: type.name,
          description: '',
          parentId: 'inventory-object-types',
          selected: false,
        }));
      return options.length ? [{
        groupId: 'inventory-object-types',
        groupName: 'TRANS__ADVANCED_SEARCH__FILTER__SELECT_OBJECT_TYPE_LABEL',
        options,
      }] : [];
    };
    this.objectTypeGroups = group(artifactMetaTypes);
    this.deletedObjectTypeGroups = group(deletedMetaTypes);
  }

  private async loadDeletedPage(): Promise<void> {
    if (this.deletedLoading || !this.deletedDateRangeValid()) {
      return;
    }
    const requestId = ++this.deletedLoadRequestId;
    this.deletedLoading = true;
    this.deletedError = false;
    try {
      await this.loadObjectTypes();
      if (requestId !== this.deletedLoadRequestId) {
        return;
      }
      const result = await this.reportApi.getDeletedPage(
        this.deletedFromDate, this.deletedToDate, 0, this.previewLimit,
        this.deletedFilterText,
        this.filterObjectTypes(this.deletedSelectedObjectTypes),
        this.deletedSortField, this.deletedSortDirection);
      if (requestId !== this.deletedLoadRequestId) {
        return;
      }
      this.deletedItems = result.items;
      this.deletedLoaded = true;
    } catch {
      if (requestId === this.deletedLoadRequestId) {
        this.deletedError = true;
      }
    } finally {
      this.deletedLoading = false;
    }
  }

  private dateValue(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // A tab switch invalidates in-flight responses as well as its timer.
  // Only this chain may schedule another poll.
  private async loadGenerationState(requestId = ++this.generationRequestId): Promise<void> {
    this.clearGenerationPoll();
    try {
      const state = await this.reportApi.getGenerationState(this.reportKind);
      if (requestId !== this.generationRequestId || this.destroyed) {
        return;
      }
      this.generationSupported = true;
      this.generationErrorMessage = '';
      this.activeGeneration = state.active;
      this.latestGeneration = state.latest;
      if (state.active) {
        this.scheduleGenerationPoll(requestId);
      }
    } catch (error: unknown) {
      if (requestId !== this.generationRequestId || this.destroyed) {
        return;
      }
      if (this.statusFromError(error) === 404) {
        // Allows a newer frontend to run safely against an older backend.
        this.generationSupported = false;
      } else {
        this.generationErrorMessage =
          'TRANS__INVENTORY_REPORT__FILE_STATUS_FAILED';
        this.scheduleGenerationPoll(requestId);
      }
    }
  }

  private scheduleGenerationPoll(requestId: number): void {
    this.clearGenerationPoll();
    const view = this.document.defaultView;
    if (view && !this.destroyed && requestId === this.generationRequestId) {
      this.generationPollTimeout = view.setTimeout(
        () => void (this.activeGeneration
          ? this.pollGeneration(this.activeGeneration.job_id, requestId)
          : this.loadGenerationState(requestId)),
        GENERATION_POLL_INTERVAL_MS,
      );
    }
  }

  private clearGenerationPoll(): void {
    if (this.generationPollTimeout === undefined) {
      return;
    }
    this.document.defaultView?.clearTimeout(this.generationPollTimeout);
    this.generationPollTimeout = undefined;
  }

  private async pollGeneration(jobId: string, requestId: number): Promise<void> {
    try {
      const generation = await this.reportApi.getGeneration(jobId, this.reportKind);
      if (requestId !== this.generationRequestId || this.destroyed) {
        return;
      }
      this.generationErrorMessage = '';
      if (generation.status === 'registered' || generation.status === 'in_progress') {
        this.activeGeneration = generation;
        this.scheduleGenerationPoll(requestId);
      } else {
        this.activeGeneration = null;
        if (generation.download_available) {
          this.latestGeneration = generation;
        } else {
          this.generationErrorMessage = 'TRANS__INVENTORY_REPORT__FILE_GENERATION_FAILED';
        }
      }
    } catch (error: unknown) {
      if (requestId !== this.generationRequestId || this.destroyed) {
        return;
      }
      if (this.statusFromError(error) === 404) {
        this.generationSupported = false;
      } else {
        this.generationErrorMessage = 'TRANS__INVENTORY_REPORT__FILE_STATUS_FAILED';
        this.scheduleGenerationPoll(requestId);
      }
    }
  }

  private exportColumns(): InventoryReportExportColumn[] {
    return (this.selectedTab === 0 ? this.displayedColumns : this.deletedColumns)
      .map(column => EXPORT_COLUMN_BY_TABLE_COLUMN[column])
      .filter((column): column is InventoryReportExportColumn => Boolean(column));
  }

  private createPrintPageStyle(): HTMLStyleElement {
    const style = this.document.createElement('style');
    style.textContent = '@page { margin: 8mm; size: A4 landscape; }';
    this.document.head.appendChild(style);
    return style;
  }

  private isInventoryReportPage(value: unknown): value is InventoryReportPage {
    if (!value || typeof value !== 'object') {
      return false;
    }
    const page = value as Partial<InventoryReportPage>;
    return Array.isArray(page.items) &&
      Number.isInteger(page.page) &&
      Number.isInteger(page.rows);
  }

  private statusFromError(error: unknown): number | undefined {
    if (error && typeof error === 'object' && 'status' in error) {
      return Number((error as {status: unknown}).status);
    }
    return undefined;
  }

  // Used only before printing. Printing exposes rows whose lazy-loaded images
  // may not have loaded while they were outside the visible table area.
  private async waitForReportImages(
    images: HTMLImageElement[],
  ): Promise<void> {
    if (!images.length) {
      return;
    }
    const imagePromises = images.map(image => this.waitForImage(image));
    const timeout = new Promise<void>(resolve =>
      window.setTimeout(resolve, 10_000),
    );
    await Promise.race([
      Promise.all(imagePromises).then(() => undefined),
      timeout,
    ]);
  }

  private getReportImages(): HTMLImageElement[] {
    return Array.from(
      this.document.querySelectorAll<HTMLImageElement>(
        '.inventory-report__thumbnail',
      ),
    );
  }

  private waitForImage(image: HTMLImageElement): Promise<void> {
    image.loading = 'eager';
    if (image.complete) {
      return Promise.resolve();
    }
    return new Promise(resolve => {
      image.addEventListener('load', () => resolve(), {once: true});
      image.addEventListener('error', () => resolve(), {once: true});
    });
  }
}
